import type { SowCandidateShape } from '@clm/shared-types';

/**
 * SOW-number shape vocabulary for the Ingest planner — CLM-67 / CLM-71.
 *
 * Kept out of the component files so Fast Refresh sees only components there.
 * {@link classifySowShape} mirrors `@clm/sharepoint`'s `classifySowCandidate`
 * (this app takes no dependency on that package): same order, same verdicts.
 * The server re-validates a typed number with the real rules and upper-cases
 * it; this mirror only keeps a hopeless value from being posted (IR-01).
 */

/** Shown as a tag only for the non-dominant shapes — a weak match is worth a look. */
export const SOW_SHAPE_LABEL: Readonly<Record<SowCandidateShape, string>> = {
  dominant_shape: 'dominant shape',
  sow_infix: 'SOW infix',
  plausible: 'plausible',
  free_text_shape: 'free text',
  too_long: 'too long',
  punctuation_only: 'punctuation only',
  no_digits: 'no digits',
  absent: 'absent',
};

/** `sow-number.ts` — the dominant IR-01 shape, e.g. `AGESOW25264OV`. */
const DOMINANT_SHAPE = /^[A-Z]{3}SOW\d{5}[A-Z]{2}$/;
const SOW_INFIX = /SOW/i;
export const MAX_SOW_NUMBER_LENGTH = 32;

export function classifySowShape(raw: string): { readonly shape: SowCandidateShape; readonly usable: boolean } {
  const value = raw.trim();
  if (value === '') return { shape: 'absent', usable: false };
  if (/\s/.test(value)) return { shape: 'free_text_shape', usable: false };
  if (value.length > MAX_SOW_NUMBER_LENGTH) return { shape: 'too_long', usable: false };
  if (!/[A-Za-z0-9]/.test(value)) return { shape: 'punctuation_only', usable: false };
  if (!/\d/.test(value)) return { shape: 'no_digits', usable: false };
  if (DOMINANT_SHAPE.test(value.toUpperCase())) return { shape: 'dominant_shape', usable: true };
  if (SOW_INFIX.test(value)) return { shape: 'sow_infix', usable: true };
  return { shape: 'plausible', usable: true };
}

/** Why an unusable shape is refused, in the operator's terms. */
export const SHAPE_REFUSAL: Readonly<Record<SowCandidateShape, string>> = {
  dominant_shape: '',
  sow_infix: '',
  plausible: '',
  free_text_shape: 'contains whitespace — a SOW number is one token, e.g. AGESOW25264OV',
  too_long: `longer than ${String(MAX_SOW_NUMBER_LENGTH)} characters — the column cannot hold it`,
  punctuation_only: 'punctuation only — not a lookup key',
  no_digits: 'carries no digit — every SOW number shape on record has at least one',
  absent: 'empty',
};
